import { useEffect, useState } from "react";
import { DownOutlined } from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COURSES, getCoursePath, getCoursesPath } from "../../courses/config";
import { PRACTICE_ITEMS, getPracticePath } from "../../practice/config";
import { getLanguageFromPath } from "../../languages";
import {
  CoursesNavItem,
  CoursesNavTrigger,
  CoursesDropdown,
  CoursesDropdownPanel,
  CoursesDropdownSection,
  CoursesDropdownSectionLabel,
  CoursesDropdownSectionLink,
  CoursesDropdownLink,
  CoursesMobileList,
  CoursesMobileExpandButton,
  CoursesMobileTopRow,
  CoursesMobileToggle,
  CoursesMobileSubmenu,
  CoursesMobileSectionLabel,
  CoursesMobileSectionLink,
  CoursesMobileSubLink,
} from "./styles";

type PracticeNavProps = {
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
  drawerOpen?: boolean;
};

const PracticeNav = ({
  variant = "desktop",
  onNavigate,
  drawerOpen = false,
}: PracticeNavProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const currentLanguage = getLanguageFromPath(location.pathname);

  useEffect(() => {
    if (!drawerOpen) {
      setMobileExpanded(false);
    }
  }, [drawerOpen]);

  const handleNavigate = () => {
    setDesktopOpen(false);
    setMobileExpanded(false);
    onNavigate?.();
  };

  if (variant === "mobile") {
    return (
      <CoursesMobileList>
        <CoursesMobileTopRow>
          <CoursesMobileToggle
            type="button"
            aria-expanded={mobileExpanded}
            onClick={() => setMobileExpanded((expanded) => !expanded)}
          >
            {t("practice.nav")}
          </CoursesMobileToggle>
          <CoursesMobileExpandButton
            type="button"
            aria-label={t("practice.mobile.toggle")}
            aria-expanded={mobileExpanded}
            onClick={() => setMobileExpanded((expanded) => !expanded)}
          >
            <DownOutlined />
          </CoursesMobileExpandButton>
        </CoursesMobileTopRow>
        {mobileExpanded && (
          <CoursesMobileSubmenu>
            <CoursesMobileSectionLink
              to={getCoursesPath(currentLanguage)}
              onClick={handleNavigate}
            >
              {t("courses.nav")}
            </CoursesMobileSectionLink>
            {COURSES.map((course) => (
              <CoursesMobileSubLink
                key={course.slug}
                to={getCoursePath(course.slug, currentLanguage)}
                onClick={handleNavigate}
              >
                {t(course.labelKey)}
              </CoursesMobileSubLink>
            ))}
            <CoursesMobileSectionLabel>
              {t("practice.other")}
            </CoursesMobileSectionLabel>
            {PRACTICE_ITEMS.map((item) => (
              <CoursesMobileSubLink
                key={item.slug}
                to={getPracticePath(item.slug, currentLanguage)}
                onClick={handleNavigate}
              >
                {t(item.labelKey)}
              </CoursesMobileSubLink>
            ))}
          </CoursesMobileSubmenu>
        )}
      </CoursesMobileList>
    );
  }

  return (
    <CoursesNavItem
      onMouseEnter={() => setDesktopOpen(true)}
      onMouseLeave={() => setDesktopOpen(false)}
    >
      <CoursesNavTrigger>
        {t("practice.nav")}
        <DownOutlined aria-hidden="true" />
      </CoursesNavTrigger>
      <CoursesDropdown $open={desktopOpen}>
        <CoursesDropdownPanel>
          <CoursesDropdownSection>
            <CoursesDropdownSectionLink
              to={getCoursesPath(currentLanguage)}
              onClick={handleNavigate}
            >
              {t("courses.nav")}
            </CoursesDropdownSectionLink>
            {COURSES.map((course) => (
              <CoursesDropdownLink
                key={course.slug}
                to={getCoursePath(course.slug, currentLanguage)}
                onClick={handleNavigate}
              >
                {t(course.labelKey)}
              </CoursesDropdownLink>
            ))}
          </CoursesDropdownSection>
          <CoursesDropdownSection>
            <CoursesDropdownSectionLabel>
              {t("practice.other")}
            </CoursesDropdownSectionLabel>
            {PRACTICE_ITEMS.map((item) => (
              <CoursesDropdownLink
                key={item.slug}
                to={getPracticePath(item.slug, currentLanguage)}
                onClick={handleNavigate}
              >
                {t(item.labelKey)}
              </CoursesDropdownLink>
            ))}
          </CoursesDropdownSection>
        </CoursesDropdownPanel>
      </CoursesDropdown>
    </CoursesNavItem>
  );
};

export default PracticeNav;
